import {
    Component,
    computed,
    effect,
    input,
    Input,
    OnDestroy,
    OnInit,
    QueryList,
    signal,
    ViewChild,
    ViewChildren
} from '@angular/core';
import {
    DateRange,
    DefaultMatCalendarRangeStrategy,
    MAT_DATE_RANGE_SELECTION_STRATEGY,
    MatCalendar
} from '@angular/material/datepicker';
import { MatMenuTrigger } from '@angular/material/menu';
import { FormControl, FormGroup } from '@angular/forms';
import dayjs from 'dayjs';
import { Subscription } from 'rxjs';
import { PeriodPickerCalendarHeaderComponent } from './components/period-picker-calendar-header';
import { PeriodPreset, PeriodPresetEnum } from './period-picker.interface';
import min from 'lodash-es/min';

import 'dayjs/locale/pl';
import { periodText } from '@shared/interactions/period-picker/helpers';

@Component({
    selector: 'period-picker',
    templateUrl: './period-picker.component.html',
    styleUrl: './period-picker.component.scss',
    standalone: false,
    providers: [
        {
            provide: MAT_DATE_RANGE_SELECTION_STRATEGY,
            useClass: DefaultMatCalendarRangeStrategy
        }
    ]
})
export class PeriodPickerComponent implements OnDestroy, OnInit {
    @ViewChildren(MatCalendar) protected calendars!: QueryList<MatCalendar<Date>>;

    @ViewChild('calendarTrigger') protected calendarTrigger!: MatMenuTrigger;

    protected customCalendarHeader = PeriodPickerCalendarHeaderComponent;

    @Input()
    public form!: FormGroup<{ from: FormControl<string>; to: FormControl<string>; }>;

    public label = input<string>('Okres');

    public icon = input<string>('arrow_drop_down');

    public presets = input<PeriodPresetEnum[]>([
        PeriodPresetEnum.LAST_24_HOURS,
        PeriodPresetEnum.LAST_7_DAYS,
        PeriodPresetEnum.LAST_30_DAYS,
        PeriodPresetEnum.LAST_3_MONTHS,
        PeriodPresetEnum.CURRENT_MONTH
    ]);

    protected selected: DateRange<Date> | null = null;

    protected currentMonth = signal(dayjs().startOf('month').toDate());

    protected previousMonth = computed(() => dayjs(this.currentMonth()).subtract(1, 'month').toDate());

    protected currentMonthLabel = computed(() => dayjs(this.currentMonth()).locale('pl').format('MMMM YYYY'));

    protected previousMonthLabel = computed(() => dayjs(this.previousMonth()).locale('pl').format('MMMM YYYY'));

    protected currentRange = signal<DateRange<Date> | null>(null);

    public minDate = input<Date | null>(dayjs().startOf('day').utc().subtract(3, 'month').toDate());

    public maxRange = input<number | null>(null);

    protected maxDate = signal<Date>(new Date());

    protected valueText = signal<string | null>(null);

    constructor() {
        effect(() => {
            const currentMonth = this.currentMonth();
            const previousMonth = this.previousMonth();

            this.calendars?.forEach((calendar, index) => {
                calendar.activeDate = index === 1 ? currentMonth : previousMonth;
            });
        });
    }

    protected selectedChange(selectedDate: Date | null): void {
        if (!this.selected?.start || this.selected?.end) {
            this.selected = new DateRange<Date>(selectedDate, null);
        } else {
            const start = this.selected.start;
            const end = selectedDate;

            if (end && end < start) {
                this.selected = new DateRange<Date>(end, null);
            } else {
                this.selected = new DateRange<Date>(start, end);
            }
        }

        const maxRange = this.maxRange();

        if (maxRange) {
            this.maxDate.set(
                this.selected.end
                    ? new Date()
                    : min<Date>([dayjs(this.selected.start).add(maxRange, 'day').toDate(), new Date()]) || new Date()
            );
        }
    }

    protected previousClicked(): void {
        this.currentMonth.update((value) => {
            if (dayjs(value).subtract(2, 'month').isBefore(this.minDate(), 'month')) {
                return value;
            }

            return dayjs(value).subtract(1, 'month').toDate();
        });
    }

    protected nextClicked(): void {
        this.currentMonth.update((value) => {
            const nextMonth = dayjs(value).add(1, 'month');

            return nextMonth.isAfter(this.maxDate(), 'month') ? value : dayjs(value).add(1, 'month').toDate();
        });
    }

    protected apply(): void {
        this.form.setValue({
            from: dayjs(this.selected?.start).startOf('day').toISOString(),
            to: dayjs(this.selected?.end ?? this.selected?.start)
                .endOf('day')
                .toISOString()
        });

        this.calendarTrigger.closeMenu();
    }

    private resetSelection(): void {
        this.selected = new DateRange<Date>(
            dayjs(this.form.controls.from.value).toDate(),
            dayjs(this.form.controls.to.value).toDate()
        );
    }

    protected cancel(): void {
        this.resetSelection();

        this.calendarTrigger.closeMenu();
    }

    private formSubscription: Subscription | null = null;

    private setValueText(from: Date, to: Date): void {
        this.valueText.set(periodText(from, to));
    }

    private setCurrentRange(from: Date, to: Date): void {
        this.currentRange.set(new DateRange<Date>(from, to));
    }

    public ngOnInit(): void {
        this.formSubscription = this.form.valueChanges.subscribe((period) => {
            if (!period.from || !period.to) {
                return;
            }

            this.setValueText(new Date(period.from), new Date(period.to));
            this.setCurrentRange(new Date(period.from), new Date(period.to));
        });

        this.resetSelection();

        if (this.form.controls.from.value) {
            this.setValueText(new Date(this.form.controls.from.value), new Date(this.form.controls.to.value));
            this.setCurrentRange(new Date(this.form.controls.from.value), new Date(this.form.controls.to.value));
        }
    }

    public ngOnDestroy(): void {
        this.formSubscription?.unsubscribe();
    }

    protected onCalendarClosed(): void {
        setTimeout(() => {
            this.resetSelection();
            this.resetCalendars();
        }, 200);
    }

    protected handlePresetSelected(preset: PeriodPreset): void {
        const from = preset.startAt();
        const to = dayjs(from).add(preset.period(), 'day').endOf('day').toDate();

        this.selected = new DateRange<Date>(from, to);
        this.form.setValue({ from: from.toISOString(), to: to.toISOString() });
        this.calendarTrigger.closeMenu();
    }

    private resetCalendars(): void {
        const from = dayjs(this.form.controls.from.value || undefined);

        this.currentMonth.set(
            dayjs(this.maxDate()).get('month') === from.get('month')
                ? from.startOf('month').toDate()
                : from.startOf('month').add(1, 'month').toDate()
        );
    }

    protected dateClass(): () => string {
        return () => 'period-picker-date';
    }
}
